import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  applyTheme,
  readStoredPreference,
  storePreference,
  subscribeSystemTheme,
  systemPrefersDark,
  type ResolvedTheme,
  type ThemePreference,
} from "./theme";

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
  /** A site's own default theme: used while the visitor has not picked one, and never stored as their choice. */
  applyDefault: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children: ReactNode;
  defaultPreference?: ThemePreference;
}

const serverPrefersDark = () => false;

/** Owns `data-theme` on <html> (light | dark | system), persisted in localStorage (best effort). */
export function ThemeProvider({ children, defaultPreference = "light" }: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => readStoredPreference() ?? defaultPreference,
  );
  const systemDark = useSyncExternalStore(subscribeSystemTheme, systemPrefersDark, serverPrefersDark);
  const resolved: ResolvedTheme = preference === "system" ? (systemDark ? "dark" : "light") : preference;

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    storePreference(next);
    setPreferenceState(next);
  }, []);

  const applyDefault = useCallback((next: ThemePreference) => {
    if (readStoredPreference() === null) setPreferenceState(next);
  }, []);

  const value = useMemo(() => ({ preference, resolved, setPreference, applyDefault }), [preference, resolved, setPreference, applyDefault]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside <ThemeProvider>");
  return context;
}
